import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase.js";
import {
  normalizeStoreImagePath,
  toPublicImagePath,
} from "../utils/storeImagePaths.js";
import { markProductRecentlyViewed } from "../utils/listingSessionStorage.js";

const FALLBACK = "https://via.placeholder.com/600x600?text=DecorZoom";
const DEFAULT_QTY = Object.freeze([1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 80, 90, 100]);
const VIDEO_EXT_RE = /\.(mp4|webm|ogg|mov|m4v)(?:$|[?#])/i;

const isVideoSrc = (src) => VIDEO_EXT_RE.test(String(src || "").trim());

const normalizeProductMediaPath = (value) => {
  const normalized = String(value || "").trim().replace(/\\/g, "/");
  if (!normalized) return "";

  if (/^https?:\/\//i.test(normalized) || /^data:/i.test(normalized)) {
    return normalized;
  }

  if (isVideoSrc(normalized)) {
    return normalized.startsWith("/") ? normalized : `/${normalized}`;
  }

  return normalizeStoreImagePath(normalized);
};

const normalizeProductMediaList = (values) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => normalizeProductMediaPath(value))
        .filter(Boolean)
    )
  );

const uniqueNumbers = (values) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0)
    )
  ).sort((a, b) => a - b);

const toMediaItem = (src) => ({
  src,
  type: isVideoSrc(src) ? "video" : "image",
});

export default function StoreProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recs, setRecs] = useState([]);

  const [activeVar, setActiveVar] = useState(0);
  const [imgIdx, setImgIdx] = useState(0);
  const [mixQty, setMixQty] = useState([]);
  const [isMediaViewerOpen, setIsMediaViewerOpen] = useState(false);

  const isMediaViewerOpenRef = useRef(false);
  const mediaViewerHistoryOpenRef = useRef(false);

  const fallbackBackTarget = location.state?.from || "/store";

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, "STORE_PRODUCTS", id));
        if (snap.exists()) {
          const data = snap.data();
          setItem(data);
          setMixQty(Array.from({ length: (data.variants || []).length }, () => 0));
        }

        const all = await getDocs(collection(db, "STORE_PRODUCTS"));
        const list = all.docs
          .filter((d) => d.id !== id)
          .map((d) => ({ id: d.id, ...d.data() }));

        list.sort(
          (a, b) =>
            (b.updatedAt || 0) - (a.updatedAt || 0) ||
            (a.title || "").localeCompare(b.title || "")
        );
        setRecs(list.slice(0, 8));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (item) {
      markProductRecentlyViewed({ ...item, id });
    }
  }, [id, item]);

  useEffect(() => {
    setImgIdx(0);
  }, [activeVar, id]);

  useEffect(() => {
    isMediaViewerOpenRef.current = isMediaViewerOpen;
  }, [isMediaViewerOpen]);

  useEffect(() => {
    if (!location.state?.from && window.history.length <= 2) {
      window.history.pushState({ dzProductBackGuard: true }, "", window.location.href);
    }

    const handlePopState = () => {
      if (mediaViewerHistoryOpenRef.current || isMediaViewerOpenRef.current) {
        mediaViewerHistoryOpenRef.current = false;
        setIsMediaViewerOpen(false);
        return;
      }

      const nextPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const currentPath = `${location.pathname}${location.search}${location.hash}`;

      if (nextPath !== currentPath) {
        return;
      }

      navigate(fallbackBackTarget, {
        replace: true,
        state: { restoreFromProduct: true },
      });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [
    fallbackBackTarget,
    location.hash,
    location.pathname,
    location.search,
    location.state?.from,
    navigate,
  ]);

  const handleBackNavigation = () => {
    if (location.state?.from || window.history.length > 2) {
      navigate(-1);
      return;
    }

    navigate(fallbackBackTarget, { replace: true });
  };

  const v = item?.variants?.[activeVar] || {};

  const priceOf = (vx) =>
    (typeof vx?.unitPrice === "number" ? vx.unitPrice : item?.unitPrice) || 0;
  const mrpOf = (vx) =>
    (typeof vx?.unitMrp === "number" ? vx.unitMrp : item?.unitMrp) || 0;

  const itemImageList = useMemo(
    () =>
      normalizeProductMediaList([
        item?.image,
        item?.mainImage,
        ...(Array.isArray(item?.images) ? item.images : []),
      ]).filter((src) => !isVideoSrc(src)),
    [item?.image, item?.mainImage, item?.images]
  );

  const itemVideoList = useMemo(
    () => normalizeProductMediaList(item?.videos),
    [item?.videos]
  );

  const colorThumbs = useMemo(() => {
    return (item?.variants || []).map((vx, i) => {
      const candidates = [
        ...normalizeProductMediaList(vx.images).filter((src) => !isVideoSrc(src)),
        ...itemImageList,
      ].filter(Boolean);

      const src = Array.from(new Set(candidates))[0] || FALLBACK;
      return { idx: i, color: vx.color || vx.variant || `Var ${i + 1}`, src };
    });
  }, [item?.variants, itemImageList]);

  const qtyOptionsByVariant = useMemo(() => {
    return (item?.variants || []).map((vx) => {
      const merged = uniqueNumbers([
        ...DEFAULT_QTY,
        ...(Array.isArray(item?.qtyOptions) ? item.qtyOptions : []),
        ...(Array.isArray(vx?.qtyOptions) ? vx.qtyOptions : []),
      ]);
      return merged.length ? merged : [...DEFAULT_QTY];
    });
  }, [item]);

  const activeThumb = colorThumbs.find((thumb) => thumb.idx === activeVar);
  const variantPreviewSrc =
    activeThumb?.src ||
    itemImageList[0] ||
    FALLBACK;

  const mediaGallery = useMemo(() => {
    const list = [
      variantPreviewSrc,
      ...normalizeProductMediaList(v.images),
      ...itemImageList,
      ...itemVideoList,
    ].filter(Boolean);

    return Array.from(new Set(list)).map((src) => toMediaItem(src));
  }, [variantPreviewSrc, v.images, itemImageList, itemVideoList]);

  const currentMedia = mediaGallery[imgIdx] || toMediaItem(variantPreviewSrc || FALLBACK);
  const currentSrc = currentMedia.src;
  const normalizedMainImage = itemImageList[0] || FALLBACK;
  const selectedCartImage =
    (currentMedia.type === "image" ? currentMedia.src : "") ||
    itemImageList[0] ||
    FALLBACK;

  const previewThumb = normalizedMainImage
    ? [{ idx: -1, color: "Preview", src: normalizedMainImage, preview: true }]
    : [];

  useEffect(() => {
    if (imgIdx > mediaGallery.length - 1) {
      setImgIdx(0);
    }
  }, [imgIdx, mediaGallery.length]);

  const openMediaViewer = (index = imgIdx) => {
    setImgIdx(index);

    if (!isMediaViewerOpenRef.current && !mediaViewerHistoryOpenRef.current) {
      window.history.pushState({ dzMediaViewer: true }, "", window.location.href);
      mediaViewerHistoryOpenRef.current = true;
    }

    setIsMediaViewerOpen(true);
  };

  const closeMediaViewer = () => {
    if (window.history.state?.dzMediaViewer) {
      window.history.back();
      return;
    }

    mediaViewerHistoryOpenRef.current = false;
    setIsMediaViewerOpen(false);
  };

  const showPrevMedia = () => {
    setImgIdx((prev) => (prev === 0 ? mediaGallery.length - 1 : prev - 1));
  };

  const showNextMedia = () => {
    setImgIdx((prev) => (prev === mediaGallery.length - 1 ? 0 : prev + 1));
  };

  const selections = useMemo(() => {
    if (!item?.variants) return [];

    return item.variants
      .map((vx, i) => {
        const qty = Number(mixQty[i] || 0);
        if (!qty) return null;

        const color = vx.color || vx.variant || `Var ${i + 1}`;
        const image =
          colorThumbs.find((t) => t.idx === i)?.src || normalizedMainImage || FALLBACK;

        return {
          idx: i,
          color,
          qty,
          unitPrice:
            (typeof vx?.unitPrice === "number" ? vx.unitPrice : item?.unitPrice) || 0,
          image: i === activeVar ? selectedCartImage : image,
        };
      })
      .filter(Boolean);
  }, [item?.variants, mixQty, colorThumbs, normalizedMainImage, activeVar, selectedCartImage, item?.unitPrice]);

  const totalUnits = selections.reduce((a, s) => a + s.qty, 0);
  const displayTotal = selections.reduce((a, s) => a + s.qty * s.unitPrice, 0);

  const getCart = () => {
    try {
      return JSON.parse(localStorage.getItem("dz_cart") || "[]");
    } catch {
      return [];
    }
  };

  const setCart = (cart) => localStorage.setItem("dz_cart", JSON.stringify(cart));

  const addToCart = () => {
    if (!selections.length || totalUnits === 0) return;

    const cart = getCart();
    selections.forEach((s) => {
      const key = `${id}::${s.idx}`;
      const existing = cart.find((it) => it.key === key);

      if (existing) {
        existing.qty += s.qty;
      } else {
        cart.push({
          key,
          productId: id,
          title: item?.title || id,
          variantIndex: s.idx,
          color: s.color,
          unitPrice: s.unitPrice,
          qty: s.qty,
          image: s.image,
          category: item?.category || "Store",
        });
      }
    });

    setCart(cart);
    alert(`Added ${totalUnits} item(s) to cart`);
    navigate("/cart");
  };

  if (loading) return <div className="p-6 text-center text-green-600">Loading...</div>;
  if (!item) return <div className="p-6 text-center text-gray-600">Product not found.</div>;

  const productTitle = item.title || id;
  const productDescription =
    item.description || `${productTitle} for party decoration by DecorZoom, delivered across Delhi NCR.`;
  const canonicalUrl = `https://www.decorzoom.in/store/product/${encodeURIComponent(id)}`;
  const absoluteImages = itemImageList.map((src) =>
    src.startsWith("http") ? src : `https://www.decorzoom.in${src}`
  );
  const schemaPrice = priceOf(v) || item.unitPrice || 0;

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: productTitle,
    description: productDescription,
    image: absoluteImages.length ? absoluteImages : undefined,
    sku: id,
    brand: { "@type": "Brand", name: "DecorZoom" },
    category: item.category || "Store",
    url: canonicalUrl,
    offers: schemaPrice
      ? {
          "@type": "Offer",
          priceCurrency: "INR",
          price: schemaPrice,
          availability: "https://schema.org/InStock",
          url: canonicalUrl,
        }
      : undefined,
  };

  return (
    <div className="mx-auto max-w-6xl px-3 py-3 sm:px-5 sm:py-6">
      <title>{`${productTitle} | DecorZoom Store`}</title>
      <meta name="description" content={productDescription.slice(0, 160)} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={productTitle} />
      <meta property="og:description" content={productDescription.slice(0, 200)} />
      {absoluteImages[0] ? <meta property="og:image" content={absoluteImages[0]} /> : null}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="product" />
      <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.decorzoom.in/" },
            { "@type": "ListItem", position: 2, name: "Store", item: "https://www.decorzoom.in/store" },
            { "@type": "ListItem", position: 3, name: productTitle, item: canonicalUrl },
          ],
        })}
      </script>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          onClick={handleBackNavigation}
          className="rounded-xl border px-3 py-2 text-sm font-medium hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={() => navigate("/")}
          className="rounded-xl border px-3 py-2 text-sm font-medium hover:bg-gray-50"
        >
          Home
        </button>
      </div>

      <div className="mt-2 grid grid-cols-1 items-start gap-4 lg:mt-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-8">
        <div className="w-full">
          <div className="mx-auto w-full max-w-xl rounded-[24px] border border-gray-200 bg-white p-2.5 shadow-sm sm:p-4 lg:max-w-none">
            <div className="overflow-hidden rounded-[20px] bg-gray-100">
              <div className="relative flex h-[220px] items-center justify-center sm:h-[320px] md:h-[400px] lg:h-[460px]">
                {currentMedia.type === "video" ? (
                  <>
                    <video
                      key={currentSrc}
                      src={currentSrc}
                      controls
                      playsInline
                      preload="metadata"
                      className="h-full w-full bg-black object-contain"
                    />
                    <button
                      type="button"
                      onClick={() => openMediaViewer(imgIdx)}
                      className="absolute bottom-3 right-3 rounded-full bg-black/65 px-3 py-1 text-[11px] font-semibold text-white"
                      aria-label="Open product media viewer"
                    >
                      Expand
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => openMediaViewer(imgIdx)}
                    className="h-full w-full"
                    aria-label="Open product media viewer"
                  >
                    <img
                      src={currentSrc}
                      alt={item.title || id}
                      className="h-full w-full bg-white object-contain"
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK;
                      }}
                    />
                  </button>
                )}

              </div>
            </div>

            {mediaGallery.length > 1 && (
              <div className="mt-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-gray-900">Product media</div>
                  <div className="text-xs text-gray-500">
                    {imgIdx + 1} / {mediaGallery.length}
                  </div>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                {mediaGallery.map((media, index) => (
                  <button
                    key={`${media.src}-${index}`}
                    type="button"
                    onClick={() => setImgIdx(index)}
                    className={`relative h-14 w-14 shrink-0 overflow-hidden rounded-2xl border bg-white sm:h-20 sm:w-20 ${
                      imgIdx === index ? "border-green-500 ring-2 ring-green-200" : "border-gray-200"
                    }`}
                    title={`Media ${index + 1}`}
                  >
                    {media.type === "video" ? (
                      <>
                        <video
                          src={media.src}
                          muted
                          playsInline
                          preload="metadata"
                          className="h-full w-full object-cover"
                        />
                        <span className="absolute inset-x-1 bottom-1 rounded-full bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
                          Video
                        </span>
                      </>
                    ) : (
                      <img
                        src={media.src}
                        alt={`${item.title || id} ${index + 1}`}
                        loading="lazy"
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = FALLBACK;
                        }}
                      />
                    )}
                  </button>
                ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-[24px] border border-gray-200 bg-white p-4 shadow-sm sm:p-5 lg:p-6">
          <h1 className="text-lg font-extrabold leading-tight text-gray-900 sm:text-2xl lg:text-3xl">
            {item.title || id}
          </h1>
          <p className="mt-1 text-sm text-gray-500">{item.category}</p>

          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
            <div className="text-2xl font-bold text-green-700 sm:text-3xl">
              Rs. {priceOf(v)}
            </div>
            {mrpOf(v) > priceOf(v) && (
              <>
                <div className="text-gray-400 line-through">Rs. {mrpOf(v)}</div>
                <div className="rounded-full bg-green-50 px-2 py-1 text-xs font-semibold text-green-700">
                  {Math.round(((mrpOf(v) - priceOf(v)) / mrpOf(v)) * 100)}% OFF
                </div>
              </>
            )}
          </div>

          {item.description ? (
            <p className="mt-3 whitespace-pre-line text-sm leading-6 text-gray-700 sm:text-base">
              {item.description}
            </p>
          ) : null}

          <div className="mt-4 rounded-2xl bg-gray-50 p-4">
            {mrpOf(v) > priceOf(v) && (
              <div className="mb-3 rounded-xl border border-green-100 bg-white px-3 py-2 text-sm text-gray-700">
                Offer price active. You save Rs. {mrpOf(v) - priceOf(v)} on this variant.
              </div>
            )}

            <div className="text-base sm:text-lg">
              <span className="font-semibold">Total:</span>{" "}
              <span className="font-bold text-green-700">Rs. {displayTotal}</span>{" "}
              <span className="text-sm text-gray-500">({totalUnits} unit)</span>
            </div>

            {totalUnits === 0 && (
              <div className="mt-1 text-xs text-gray-500">
                Select quantity color-wise below.
              </div>
            )}

            <div className="mt-3 space-y-1 text-sm text-gray-700">
              {selections.map((s) => (
                <div key={`${s.idx}-${s.color}`}>
                  {s.color}: {s.qty} x Rs. {s.unitPrice} = Rs. {s.qty * s.unitPrice}
                </div>
              ))}
            </div>

            <button
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-green-600 px-5 py-3.5 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              disabled={totalUnits === 0}
              onClick={addToCart}
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-[24px] border border-gray-200 bg-white p-4 shadow-sm sm:mt-8 sm:p-5">
        <div className="mb-3 text-sm font-semibold text-gray-900">Choose color</div>

        <div className="grid grid-cols-3 gap-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9 xl:grid-cols-10">
          {previewThumb.map((t) => (
            <div key="preview-thumb" className="flex min-w-0 flex-col items-center">
              <div className="h-14 w-14 overflow-hidden rounded-xl border bg-white sm:h-16 sm:w-16">
                <img
                  src={t.src}
                  alt={`${productTitle} preview`}
                  loading="lazy"
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK;
                  }}
                />
              </div>
              <span className="mt-1 line-clamp-1 text-[10px] text-gray-700 sm:text-[11px]">
                Preview
              </span>
              <select
                className="mt-1 w-full max-w-[84px] cursor-not-allowed rounded-lg border px-1 py-1 text-[11px] opacity-60"
                disabled
                value={0}
                onChange={() => {}}
              >
                <option>0</option>
              </select>
            </div>
          ))}

          {colorThumbs.map((t) => (
            <div key={t.idx} className="flex min-w-0 flex-col items-center">
              <button
                onClick={() => {
                  setActiveVar(t.idx);
                  setImgIdx(0);
                }}
                className={`h-14 w-14 overflow-hidden rounded-xl border bg-white sm:h-16 sm:w-16 ${
                  activeVar === t.idx ? "ring-2 ring-green-500" : ""
                }`}
                title={t.color}
              >
                <img
                  src={t.src}
                  alt={`${productTitle} in ${t.color}`}
                  loading="lazy"
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK;
                  }}
                />
              </button>
              <span className="mt-1 line-clamp-1 max-w-[84px] text-center text-[10px] text-gray-700 sm:text-[11px]">
                {t.color}
              </span>
              <select
                className="mt-1 w-full max-w-[84px] rounded-lg border px-1 py-1 text-[11px]"
                value={mixQty[t.idx] ?? 0}
                onChange={(e) => {
                  const q = Number(e.target.value);
                  setMixQty((prev) => {
                    const next = [...prev];
                    next[t.idx] = q;
                    return next;
                  });
                }}
                title={`Qty for ${t.color}`}
              >
                {[0, ...(qtyOptionsByVariant[t.idx] || DEFAULT_QTY)].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <div className="mt-3 text-xs text-gray-500">
          Selected: <span className="font-semibold">{v.color || v.variant || "-"}</span>
        </div>
      </div>

      {recs.length > 0 && (
        <div className="mt-10">
          <h2 className="mb-3 text-lg font-semibold">You may also like</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4">
            {recs.map((p) => {
              const v0 = p.variants?.[0] || {};
              const primaryImg =
                normalizeStoreImagePath(v0.images?.[0]) ||
                normalizeStoreImagePath(p.mainImage) ||
                (v0.color
                  ? toPublicImagePath(
                      `${String(v0.color).toLowerCase().replace(/\s+/g, "-")}.jpg`
                    )
                  : "") ||
                FALLBACK;
              const price =
                (typeof p.unitPrice === "number" ? p.unitPrice : null) ??
                (typeof v0.unitPrice === "number" ? v0.unitPrice : null) ??
                0;
              const mrp =
                (typeof p.unitMrp === "number" ? p.unitMrp : null) ??
                (typeof v0.unitMrp === "number" ? v0.unitMrp : null) ??
                0;
              const discount =
                mrp && price && price < mrp
                  ? Math.round(((mrp - price) / mrp) * 100)
                  : null;

              return (
                <article
                  key={p.id}
                  onClick={() => navigate(`/store/product/${encodeURIComponent(p.id)}`)}
                  className="group cursor-pointer overflow-hidden rounded-xl border bg-white shadow-sm transition hover:shadow-md"
                  title="View details"
                >
                  <div className="aspect-square bg-gray-100">
                    <img
                      src={primaryImg}
                      alt={p.title || p.id}
                      loading="lazy"
                      className="h-full w-full object-contain transition-transform group-hover:scale-[1.02]"
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK;
                      }}
                    />
                  </div>
                  <div className="p-2">
                    <div className="line-clamp-2 text-sm font-semibold">{p.title}</div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="font-bold">Rs. {price}</span>
                      {discount && (
                        <>
                          <span className="text-xs text-gray-400 line-through">Rs. {mrp}</span>
                          <span className="text-[10px] font-semibold text-green-600">
                            {discount}% OFF
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      )}

      {isMediaViewerOpen && (
        <div className="fixed inset-0 z-50 bg-black/90">
          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between px-3 py-3 text-white sm:px-5">
              <div className="min-w-0 pr-3">
                <div className="truncate text-sm font-semibold">{item.title || id}</div>
                <div className="text-xs text-white/70">
                  {imgIdx + 1} / {mediaGallery.length}
                </div>
              </div>
              <button
                type="button"
                onClick={closeMediaViewer}
                className="rounded-full border border-white/20 bg-white/10 px-3 py-2 text-sm font-semibold"
                aria-label="Close media viewer"
              >
                Close
              </button>
            </div>

            <div className="relative flex min-h-0 flex-1 items-center justify-center px-3 pb-3 sm:px-6 sm:pb-6">
              {mediaGallery.length > 1 && (
                <button
                  type="button"
                  onClick={showPrevMedia}
                  className="absolute left-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-2xl text-white backdrop-blur sm:left-4"
                  aria-label="Previous media"
                >
                  &#8249;
                </button>
              )}

              <div className="flex h-full w-full max-w-5xl items-center justify-center overflow-hidden rounded-[24px] bg-black">
                {currentMedia.type === "video" ? (
                  <video
                    key={`viewer-${currentSrc}`}
                    src={currentSrc}
                    controls
                    playsInline
                    autoPlay
                    preload="metadata"
                    className="max-h-full w-full object-contain"
                  />
                ) : (
                  <img
                    src={currentSrc}
                    alt={item.title || id}
                    className="max-h-full w-full object-contain"
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK;
                    }}
                  />
                )}
              </div>

              {mediaGallery.length > 1 && (
                <button
                  type="button"
                  onClick={showNextMedia}
                  className="absolute right-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-2xl text-white backdrop-blur sm:right-4"
                  aria-label="Next media"
                >
                  &#8250;
                </button>
              )}
            </div>

            {mediaGallery.length > 1 && (
              <div className="flex gap-2 overflow-x-auto px-3 pb-4 sm:px-5">
                {mediaGallery.map((media, index) => (
                  <button
                    key={`viewer-thumb-${media.src}-${index}`}
                    type="button"
                    onClick={() => setImgIdx(index)}
                    className={`relative h-14 w-14 shrink-0 overflow-hidden rounded-2xl border ${
                      imgIdx === index ? "border-green-400 ring-2 ring-green-300/50" : "border-white/15"
                    }`}
                    aria-label={`View media ${index + 1}`}
                  >
                    {media.type === "video" ? (
                      <>
                        <video
                          src={media.src}
                          muted
                          playsInline
                          preload="metadata"
                          className="h-full w-full object-cover"
                        />
                        <span className="absolute inset-x-1 bottom-1 rounded-full bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
                          Video
                        </span>
                      </>
                    ) : (
                      <img
                        src={media.src}
                        alt={`${item.title || id} ${index + 1}`}
                        loading="lazy"
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = FALLBACK;
                        }}
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
